//React imports
import { useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';

// External libraries

import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import { Button } from 'foundations-components/transitional/components/Button';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import styled, { useTheme } from 'foundations-theming/utils';

// MeetingsBase Models and utils

import { getTextColor } from 'MeetingsBase/utils/color';

// Redux selectors
import { getHasMoreLinkAvailabilityByMonthOffset, getCustomAvailabilityStartDate, getCustomAvailabilityEndDate, getPushbackTime } from 'MeetingsLib/selectors/BookInfoSelectors';
import { useCurrentMonthOffset } from '../../../context/PublicAvailabilityInfoContext';
// Types

import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { LeftIcon } from 'foundations-assets/icons/components/LeftIcon';
import { RightIcon } from 'foundations-assets/icons/components/RightIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function getMonthButtonOpacity(disabled) {
  return disabled ? '0.5' : '1';
}
const MonthButtonIconWrapper = styled.span.withConfig({
  displayName: "DatePickerHeaderV2__MonthButtonIconWrapper"
})(["opacity:", ";color:", ";"], props => props.opacity, props => props.color);
function DatePickerHeader({
  startOfMonth,
  availableDateOptions,
  setMonthsAhead,
  isTroubleshooting,
  compact,
  branding,
  isLoadingLinkAvailabilityPage,
  hasCustomAvailability,
  renderedMonthLabelRef,
  isPreviewed
}) {
  const theme = useTheme();
  const hasMoreByOffset = useSelector( /*  @ts-expect-error Jun-18-2025, 19:10UTC fix types  */
  getHasMoreLinkAvailabilityByMonthOffset);
  const currentMonthOffset = useCurrentMonthOffset();
  const hasMoreAtCurrentOffset = useMemo(() => {
    return hasMoreByOffset.get(`${currentMonthOffset}`);
  }, [currentMonthOffset, hasMoreByOffset]);
  const customAvailabilityStartDate = useSelector( /*  @ts-expect-error Jun-18-2025, 19:10UTC fix types  */
  getCustomAvailabilityStartDate);
  const customAvailabilityEndDate = useSelector( /*  @ts-expect-error Jun-18-2025, 19:10UTC fix types  */
  getCustomAvailabilityEndDate);
  /*  @ts-expect-error Jun-18-2025, 19:10UTC fix types  */
  const pushBackTime = useSelector(getPushbackTime);

  // minMonth is determined by the current month when there's no custom availability
  // otherwise it is determined by the custom availableStartTime eturned by the BE
  const isMinMonth = useMemo(() => {
    const customAvailabilityStartTime = customAvailabilityStartDate + pushBackTime;
    const expiredCustomAvailabilityStartTime = customAvailabilityStartDate < Date.now();
    if (!hasCustomAvailability || expiredCustomAvailabilityStartTime) {
      return !isTroubleshooting && I18n.moment().month() === startOfMonth.month() && I18n.moment().year() === startOfMonth.year();
    }
    return !isTroubleshooting && I18n.moment(customAvailabilityStartTime).utc().month() === startOfMonth.month() && I18n.moment(customAvailabilityStartTime).utc().year() === startOfMonth.year();
  }, [customAvailabilityStartDate, hasCustomAvailability, isTroubleshooting, startOfMonth, pushBackTime]);
  const isMaxMonth = useMemo(() => {
    if (isPreviewed) {
      return I18n.moment(availableDateOptions.max()).month() === startOfMonth.month() && I18n.moment(availableDateOptions.max()).year() === startOfMonth.year();
    }
    if (isTroubleshooting) {
      return false;
    }
    if (hasCustomAvailability) {
      const end = I18n.moment(customAvailabilityEndDate).utc();
      return startOfMonth.isSameOrAfter(end, 'month');
    }
    return !hasMoreAtCurrentOffset;
  }, [isPreviewed, isTroubleshooting, availableDateOptions, startOfMonth, hasCustomAvailability, customAvailabilityEndDate, hasMoreAtCurrentOffset]);
  const monthBackOpacity = getMonthButtonOpacity(isMinMonth);
  const monthForwardOpacity = getMonthButtonOpacity(isMaxMonth);
  const textColor = getTextColor(branding.primaryColor, theme);
  const textColorToken = getTextColor(branding.primaryColor, theme, true);
  const currentMonthName = startOfMonth.format('MMMM YYYY');
  const previousMonthName = startOfMonth.clone().add(-1, 'months').format('MMMM');
  const nextMonthName = startOfMonth.clone().add(1, 'months').format('MMMM');
  const getPrevMonth = useCallback(() => {
    // @ts-expect-error TODO
    setMonthsAhead(_monthsAhead => _monthsAhead - 1);
  }, [setMonthsAhead]);
  const getNextMonth = useCallback(() => {
    // @ts-expect-error TODO
    setMonthsAhead(_monthsAhead => _monthsAhead + 1);
  }, [setMonthsAhead]);
  return /*#__PURE__*/_jsxs(Flex, {
    alignItems: "center",
    justifyContent: "center",
    marginBlockEnd: compact ? '400' : '600',
    children: [/*#__PURE__*/_jsx(Tooltip, {
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "prebook.datePicker.noPreviousMonthReason",
        options: {
          month: previousMonthName
        }
      }),
      disabled: !isMinMonth,
      placement: "right",
      children: /*#__PURE__*/_jsx(Button, {
        use: "transparent",
        "data-selenium-test": "previous-month-button",
        onClick: getPrevMonth,
        disabled: isMinMonth,
        "aria-label": I18n.text('prebook.datePicker.previousMonth', {
          month: previousMonthName
        }),
        "data-test-id": "date-picker-header-prev-btn",
        paddingBlockStart: "400",
        children: /*#__PURE__*/_jsx(MonthButtonIconWrapper, {
          className: "month-button-icon",
          opacity: monthBackOpacity,
          color: textColor,
          children: /*#__PURE__*/_jsx(LeftIcon, {
            size: "100"
          })
        })
      })
    }), /*#__PURE__*/_jsx(View, {
      as: "span",
      minInlineSize: compact ? '200px' : '80px',
      children: /*#__PURE__*/_jsx(Text, {
        as: "h3",
        "aria-label": currentMonthName,
        color: textColorToken,
        "data-selenium-test": "month-and-year",
        "aria-level": 2,
        marginBlockEnd: "0",
        textAlign: "center",
        children: /*#__PURE__*/_jsx("span", {
          ref: renderedMonthLabelRef,
          "data-test-id": "current-month-label",
          children: currentMonthName
        })
      })
    }), /*#__PURE__*/_jsx(Tooltip, {
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "prebook.datePicker.noPreviousMonthReason",
        options: {
          month: nextMonthName
        }
      }),
      disabled: !isMaxMonth || isLoadingLinkAvailabilityPage,
      placement: "left",
      children: /*#__PURE__*/_jsx(Button, {
        use: "transparent",
        onClick: getNextMonth,
        disabled: isMaxMonth || isLoadingLinkAvailabilityPage,
        "aria-label": I18n.text('prebook.datePicker.nextMonth', {
          month: nextMonthName
        }),
        "data-selenium-test": "next-month-button",
        "data-test-id": "date-picker-header-next-btn",
        paddingBlockStart: "400",
        children: /*#__PURE__*/_jsx(MonthButtonIconWrapper, {
          className: "month-button-icon",
          opacity: monthForwardOpacity,
          color: textColor,
          children: /*#__PURE__*/_jsx(RightIcon, {
            size: "100"
          })
        })
      })
    })]
  });
}
export default DatePickerHeader;